import type { AuthProvider } from '@refinedev/core';
import { apiUrl } from '../utils/apiUrl';
import { tokenManager } from '../utils/tokenManager';

/**
 * Refine Auth Provider — 對接後端 /api/admin/auth 端點
 */
export const authProvider: AuthProvider = {
  /**
   * 登入 — POST /api/admin/auth/login
    */
  login: async ({ username, password }) => {
    try {
      const response = await fetch(apiUrl('/api/admin/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const result = await response.json();

      if (result.code === 0 && result.data) {
        const { token, refreshToken, expiresIn, mustChangePassword } =
          result.data;
        tokenManager.setTokens(
          token,
          refreshToken,
          expiresIn,
          !!mustChangePassword,
        );
        return {
          success: true,
          redirectTo: mustChangePassword ? "/force-password" : "/",
        };
      }

      return {
        success: false,
        error: {
          name: "登入失敗",
          message: result.msg || "帳號或密碼錯誤",
        },
      };
    } catch {
      return {
        success: false,
        error: {
          name: "登入失敗",
          message: "網路錯誤，請稍後再試",
        },
      };
    }
  },

  /**
   * 登出 — POST /api/auth/logout
   */
  logout: async () => {
    const refreshToken = tokenManager.getRefreshToken();
    const accessToken = tokenManager.getRawToken();

    // 清除本地 token
    tokenManager.clear();

    // 通知後端撤銷 refresh token
    if (refreshToken && accessToken) {
      try {
        await fetch(apiUrl('/api/auth/logout'), {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // 登出失敗不影響前端流程
      }
    }

    return {
      success: true,
      redirectTo: "/manage",
    };
  },

  /**
   * 檢查認證狀態 — 驗證 token 是否存在且有效
   */
  check: async () => {
    const token = await tokenManager.getToken();
    if (token) {
      return {
        authenticated: true,
        redirectTo: tokenManager.mustChangePasswordRequired()
          ? "/force-password"
          : undefined,
      };
    }
    return {
      authenticated: false,
      redirectTo: "/manage",
      error: {
        name: "未認證",
        message: "請先登入",
      },
    };
  },

  /**
   * 錯誤處理 — 401 時跳轉登入頁
   */
  onError: async (error) => {
    const status = error?.statusCode || error?.status;
    if (status === 401) {
      tokenManager.clear();
      return {
        logout: true,
        redirectTo: "/manage",
        error: {
          name: "認證過期",
          message: "請重新登入",
        },
      };
    }
    return { error };
  },

  /**
   * 取得使用者身份資訊
   */
  getIdentity: async () => {
    const token = tokenManager.getRawToken();
    if (!token) return null;

    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return {
        id: payload.userId,
        name: payload.username || "管理員",
        activeBrand: payload.activeBrand || payload.brand,
        brandScope: payload.brandScope,
        mustChangePassword: tokenManager.mustChangePasswordRequired(),
      };
    } catch {
      return null;
    }
  },

  /**
   * 取得權限
   */
  getPermissions: async () => {
    const token = tokenManager.getRawToken();
    if (!token) return null;

    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload.type || "admin";
    } catch {
      return null;
    }
  },
};
